import React from 'react';
import { TFunction } from 'i18next';
import { Chip, DataTableMeta, ElementsGroup } from '@otp/ui';
import { ActionsColumn } from './columns/actions';
import { LoginRuleCriteriaAction } from 'src/components/login-rules/form/login-rule-criteria/table/columns/actions/actions-modals';
import { LoginRuleCriteria } from 'src/components/login-rules/form/types';
import { LoginRuleUserPoolFieldPayload } from 'src/graphql/generated/graphql';
import { LoginRuleUserPoolFieldsPayload } from '../types';
import { capitalize } from 'src/utils/string';

const getValueFromData = ({
  value,
  name,
  data,
}: {
  value: any;
  name: keyof LoginRuleUserPoolFieldsPayload;
  data: LoginRuleUserPoolFieldsPayload;
}) => {
  const dataOptions: LoginRuleUserPoolFieldPayload[] = data
    ? (data[name] as LoginRuleUserPoolFieldPayload[])
    : [];
  const dataOption = dataOptions?.find((option) => option.id === value);

  return dataOption?.name;
};

export const getTableConfig = (
  loginRuleCriteria: LoginRuleCriteria[],
  {
    t,
    onActionFinished,
    data,
  }: {
    t: TFunction;
    onActionFinished?: (
      action: LoginRuleCriteriaAction,
      { data, index }: { data: LoginRuleCriteria; index: number },
    ) => void;
    data: LoginRuleUserPoolFieldsPayload;
  },
) => [
  {
    name: 'managementRoles',
    label: 'Management Role',
    options: {
      customBodyRender: (managementRoles: string[], column: DataTableMeta) => {
        const { name } = column.columnData;

        const entities = managementRoles?.map((roleId) =>
          getValueFromData({ value: roleId, name: name as any, data: data }),
        );

        return (
          entities &&
          entities.length > 0 && (
            <ElementsGroup
              elements={entities.map((entity) => (
                <Chip key={entity} label={entity} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'companyTypes',
    label: 'Company Type',
    options: {
      customBodyRender: (companyTypes: string[], column: DataTableMeta) => {
        const { name } = column.columnData;

        const entities = companyTypes?.map((companyType) =>
          getValueFromData({
            value: companyType,
            name: name as any,
            data: data,
          }),
        );

        return (
          entities &&
          entities.length > 0 && (
            <ElementsGroup
              elements={entities.map((entity) => (
                <Chip key={entity} label={entity} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'companyNames',
    label: 'Company name contains',
    options: {
      customBodyRender: (companyNames: string[]) => {
        return (
          companyNames &&
          companyNames.length > 0 && (
            <ElementsGroup
              elements={companyNames?.map((name) => (
                <Chip key={name} label={name} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'countries',
    label: 'Countries',
    options: {
      customBodyRender: (countryIds: string[], column: DataTableMeta) => {
        const { name } = column.columnData;

        const countries = countryIds?.map((countryId) =>
          getValueFromData({ value: countryId, name: name as any, data: data }),
        );

        return (
          countries &&
          countries.length > 0 && (
            <ElementsGroup
              elements={countries.map((country) => (
                <Chip key={country} label={country} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'fieldOffices',
    label: 'Locale',
    options: {
      customBodyRender: (fieldOfficeIds: string[], column: DataTableMeta) => {
        const { name } = column.columnData;

        const entities = fieldOfficeIds?.map((id) =>
          getValueFromData({ value: id, name: name as any, data: data }),
        );

        return (
          entities &&
          entities.length > 0 && (
            <ElementsGroup
              elements={entities.map((entity) => (
                <Chip key={entity} label={entity} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'programs',
    label: 'Program',
    options: {
      customBodyRender: (programIds: string[], column: DataTableMeta) => {
        const { name } = column.columnData;

        const programLevels = programIds?.map((id) =>
          getValueFromData({ value: id, name: name as any, data: data }),
        );

        return (
          programLevels &&
          programLevels.length > 0 && (
            <ElementsGroup
              elements={programLevels.map((programLevel) => (
                <Chip key={programLevel} label={programLevel} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'userLevels',
    label: 'User Level',
    options: {
      customBodyRender: (_: string[], column: DataTableMeta) => {
        const rowData = loginRuleCriteria[column.rowIndex];
        const userLevels = rowData?.['programLevels'] || [];

        return (
          !!userLevels?.length && (
            <ElementsGroup
              elements={userLevels.map((userLevel: string) => (
                <Chip
                  key={userLevel}
                  label={t(
                    `common.otp-levels.${capitalize(userLevel).replace('-', '')}`,
                  )}
                />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'certificates',
    label: 'Certificates',
    options: {
      customBodyRender: (certificateIds: string[], column: DataTableMeta) => {
        const certificates = certificateIds?.map((certificateId) =>
          getValueFromData({
            value: certificateId,
            name: 'certificates',
            data: data,
          }),
        );

        return (
          !!certificates?.length && (
            <ElementsGroup
              elements={certificates.map((certificate) => (
                <Chip key={certificate} label={certificate} />
              ))}
              limit={3}
            />
          )
        );
      },
    },
  },
  {
    name: 'actions',
    label: t('common.actions'),
    options: {
      customBodyRender: (_: any, column: DataTableMeta) => {
        const item = loginRuleCriteria[column.rowIndex];

        return (
          <ActionsColumn
            loginRuleCriteria={item}
            onActionFinished={onActionFinished}
            index={column.rowIndex}
          />
        );
      },
    },
  },
];
